// src/pages/publicTab/DivisionsTab.jsx

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingSpinner from '../../components/LoadingSpinner';
import Division from '../private/admin/orgStructureTabs/Division';
import useIsMobile from '../../hooks/isMobile';

const default_text = `Join our paramilitary organization and explore...`;

export default function DivisionsTab() {
  const [organizationId]       = useState('1');
  const [divisions, setDivisions]           = useState(null);
  const [selectedDivisionId, setSelectedDivisionId] = useState('1');
  const [expandedOrder, setExpandedOrder]   = useState("1"); 
  const [divisionDescription, setDivisionDescription] = useState(default_text);
  const [divisionFullName, setDivisionFullName]       = useState('OUR DIVISIONS');
  const [divisionImageUrl, setDivisionImageUrl]       = useState('/images/logo.png');
  const isMobile = useIsMobile();

  // Reset on click outside
  useEffect(() => {
    const resetDescription = () => {
      setSelectedDivisionId(null);
      setDivisionDescription(default_text);
      setDivisionFullName('OUR DIVISIONS');
      setDivisionImageUrl(`${process.env.STATIC_HOST}/files/logo/medium/PISC.png`);
    };
    window.addEventListener('click', resetDescription);
    return () => window.removeEventListener('click', resetDescription);
  }, []);

  // Fetch divisions
  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        const { data } = await axios.get('/api/admin/get-org-structure', { params: { organizationId } });
        setDivisions(data.divisions);
        const minOrder = Math.min(
          ...data.divisions
            .filter(d => d.order >= 2 && d.order <= 6)
            .map(d => d.order)
        );
        setExpandedOrder(minOrder.toString());
      } catch (error) {
        console.error('Failed to fetch data', error);
      }
    };
    fetchDivisions();
  }, [organizationId]);

  // Handle click on a division
  const onDivisionClick = (divisionId) => {
    const sel = divisions.find(d => d.id === divisionId);
    setSelectedDivisionId(divisionId);
    setDivisionDescription(sel?.divisionDescription ?? "Nothing yet");
    setDivisionFullName(sel?.divisionFullName ?? "DIVISION DESCRIPTION");
    setDivisionImageUrl(sel?.divisionImageUrl || null);
    if (sel?.order >= 2 && sel.order <= 6) {
      setExpandedOrder(sel.order);
    }
  };

  // Stable root container with key (class also used in publicPages.scss :has() to widen .public-container on large viewports only)
  return (
    <div
      className="divisions-tab-root"               /*  stable root container */
      key={divisions ? "loaded" : "spinner"}      /*  force remount on data load */
    >
      {divisions === null ? (
        <LoadingSpinner />                         /*  show spinner while loading divisions */
      ) : (
        <div className="divisions-tab">
          {isMobile ? (
            <div className="mobile-view">
              <div className="division-description-logo">
                {divisionImageUrl && (
                  <img
                    src={divisionImageUrl}
                    alt="Division"
                    className="division-logo"
                  />
                )}
                <h3 className="division-title">{divisionFullName}</h3>
              </div>
              <div className="division-description-content">
                <p className="division-description">
                  {divisionDescription}
                </p>
              </div>
            </div>
          ) : (
            <div className="desktop-view">
              <div className="division-description-logo">
                <h3 className="division-title">{divisionFullName}</h3>
                {divisionImageUrl && (
                  <img
                    src={divisionImageUrl}
                    alt="Division"
                    className="division-logo"
                  />
                )}
              </div>
              <div className="division-description-content">
                <p className="division-description">
                  {divisionDescription}
                </p>
              </div>
            </div>
          )}

          <div className="organization-structure">
            <div className="tree-row" onClick={e => e.stopPropagation()}>
              {divisions
                .filter(d => !d.parentId)
                .map(node => (
                  <Division
                    key={node.id}                /*  stable key for each node */
                    division={node}
                    divisions={divisions}
                    onNodeClick={onDivisionClick}
                    selectedDivisionId={selectedDivisionId}
                    expandedOrder={expandedOrder}
                  />
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
